/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useMemo, useState } from 'react'
import { MATCH_STATUS, SPORT_ID } from 'adv-protocol/src/GameCommon'
import _ from 'lodash'
import uuid from 'uuid'
import { SportsIco, TimeLineWrap } from '@styles/match.style'
import { SPORT_ID_ICON_MAP } from '@shared/SportIdGroup'
import { GoalScorer, PeriodInfo, SimpleMatchTimeline, TimelineEventType } from '@interface/chat'
import { observer } from 'mobx-react'
import { MatchRecordSimpleMatchInfo } from '@views/MatchRecord/index'
import classNames from 'classnames'
import { Tabs } from 'antd-mobile'
import DialogUtils from '@utils/dialog-utils'
import { Button } from '@styles/button.style'
//img
import scoreImg from '@assets/images/icon/soccer_timeline_icon/score.png'
import scoreGrayImg from '@assets/images/icon/soccer_timeline_icon/score-gray.png'
import cornerKickImg from '@assets/images/icon/soccer_timeline_icon/corner-kick.png'
import cornerKickGrayImg from '@assets/images/icon/soccer_timeline_icon/corner-kick-gray.png'
import cardImg from '@assets/images/icon/soccer_timeline_icon/card.png'
import cardGrayImg from '@assets/images/icon/soccer_timeline_icon/card-gray.png'
import yellowCardImg from '@assets/images/icon/soccer_timeline_icon/yellow-card.png'
import redCardImg from '@assets/images/icon/soccer_timeline_icon/red-card.png'
import showCardCountGuideImg from '@assets/images/btn/card-count-guide-btn.png'
import { EMPTY_ICON } from '@constants/index'
import { AdvEmpty } from '@components/AdvEmpty'

interface TimelineHistory {
  id: number
  time: number | string
  type?: string
  visibleIco?: React.ReactElement
  periodName?: string
  period?: number
  team: string | 'home' | 'away'
  playerName: string
  goalMethod?: string
  totalScore?: number
  emptyClass?: string
  matchStatusCode?: number
}

export interface Props {
  matchRecordSimpleMatchInfo: MatchRecordSimpleMatchInfo
  simpleMatchTimeline: SimpleMatchTimeline
}

const SOCCER_TAB_KEY = {
  SCORE: 'SCORE',
  CORNER_KICK: 'CORNER_KICK',
  CARD: 'CARD',
}

const SOCCER_TIMELINE_ICON_MAP_KEY = {
  SCORE: 'SCORE',
  CORNER_KICK: 'CORNER_KICK',
  YELLOW_RED_CARD: 'YELLOW_RED_CARD',
  YELLOW_CARD: 'YELLOW_CARD',
  RED_CARD: 'RED_CARD',
}

const SOCCER_TIMELINE_ICON_MAP = {
  [SOCCER_TIMELINE_ICON_MAP_KEY.SCORE]: <img src={scoreImg} className="sport-ico" alt="score score"/>,
  [SOCCER_TIMELINE_ICON_MAP_KEY.CORNER_KICK]: <img src={cornerKickImg} className="sport-ico" alt="score corner kick"/>,
  [SOCCER_TIMELINE_ICON_MAP_KEY.YELLOW_RED_CARD]: <img src={cardImg} className="sport-ico" alt="score card"/>,
  [SOCCER_TIMELINE_ICON_MAP_KEY.YELLOW_CARD]: <img src={yellowCardImg} className="sport-ico" alt="score yellow card"/>,
  [SOCCER_TIMELINE_ICON_MAP_KEY.RED_CARD]: <img src={redCardImg} className="sport-ico" alt="score red card"/>,
}

export const MatchTimeline: React.FC<Props> = observer((props: Props) => {
  const {
    matchRecordSimpleMatchInfo,
    simpleMatchTimeline = { periodInfoList: [] as PeriodInfo[] },
  } = props

  const [activeTabKey, setActiveTabKey] = useState(SOCCER_TAB_KEY.SCORE)
  const isCardTab = activeTabKey === SOCCER_TAB_KEY.CARD

  const { sportId, homeTeamName, awayTeamName } = matchRecordSimpleMatchInfo
  const { periodInfoList = [] } = simpleMatchTimeline
  let isDisabledTab = false

  const makeSoccerHistories = (): TimelineHistory[] => {
    const histories: TimelineHistory[] = []
    _.forEach(periodInfoList, periodInfo => {
      for (const timeEvent of periodInfo.timelineEventList) {
        const type = timeEvent.type

        if (type === TimelineEventType.MATCH_ENDED) {
          break
        }

        if (!(type === TimelineEventType.SCORE_CHANGE
          || type === TimelineEventType.CORNER_KICK
          || type === TimelineEventType.RED_CARD
          || type === TimelineEventType.YELLOW_CARD
          || type === TimelineEventType.YELLOW_RED_CARD)) {
          continue
        }

        const time = timeEvent.matchTime
        const team = timeEvent.team
        let playerName

        if (timeEvent.type === TimelineEventType.SCORE_CHANGE) {
          const goalScorer = timeEvent.goalScorer
          if (!_.isEmpty(goalScorer)) {
            playerName = goalScorer?.name
            if (_.toLower(goalScorer.method) === 'penalty') {
              playerName = `(P)${playerName}`
            }
          }
        } else {
          playerName = timeEvent.player?.name
        }

        histories.push({
          id: timeEvent.id,
          time, team, playerName,
          type, matchStatusCode: periodInfo.matchStatusCode,
        })
      }
    })
    return histories
  }

  const fillEmptyData = (scoreInfoList: TimelineHistory[], count: number, team: 'home' | 'away', period: number): void => {
    _.range(count).forEach(() => {
      scoreInfoList.push({
        id: uuid.v4(), time: uuid.v4(), team, playerName: null, periodName: null, period, emptyClass: 'empty',
      })
    })
  }
  const makeBaseballScoreHistories = (): TimelineHistory[] => {
    const scoreInfoList: TimelineHistory[] = []
    let prevPeriodName = ''
    _.forEach(periodInfoList, periodInfo => {
      let homeCount = 0
      let awayCount = 0
      let periodName = periodInfo.matchStatusName
      let period = periodInfo.number ?? _.toNumber(periodName.substring(0, 1))
      for (const timeEvent of periodInfo.timelineEventList) {
        if (!_.isNil(timeEvent.period) && _.isNil(period)) {
          period = timeEvent.period
        }
        const assistList = timeEvent.assistList
        const goalScorer = timeEvent.goalScorer
        if (!_.isEmpty(assistList) || !_.isEmpty(goalScorer)) {
          if (prevPeriodName !== periodName) {
            prevPeriodName = periodName
          } else if (prevPeriodName === periodName) {
            periodName = null
          }
          const time = timeEvent.matchTime
          const team = timeEvent.team
          let playerName = ''
          if (!_.isEmpty(assistList)) {
            const assist = assistList[0]
            playerName = `${assist.name}`
          } else if (!_.isEmpty(goalScorer)) {
            playerName = `${goalScorer.name}`
          }
          scoreInfoList.push({
            id: timeEvent.id,
            time, team, playerName, periodName, period, emptyClass: '',
          })
          if (team === 'home') {
            homeCount += 1
          }
          if (team === 'away') {
            awayCount += 1
          }
        }
      }
      if (homeCount > awayCount) {
        fillEmptyData(scoreInfoList, homeCount - awayCount, 'away', period)
      } else if (homeCount < awayCount) {
        fillEmptyData(scoreInfoList, awayCount - homeCount, 'home', period)
      }
    })
    return scoreInfoList
  }
  const makeIceHockeyScoreHistories = (): TimelineHistory[] => {
    const scoreInfoList: TimelineHistory[] = []
    let prevPeriodName = ''
    _.forEach(periodInfoList, periodInfo => {
      let periodName = periodInfo.matchStatusName
      if (periodName.includes('피리어드')) {
        periodName = periodName.replace('피리어드', 'P')
      }
      let homeCount = 0
      let awayCount = 0
      let period = periodInfo.number ?? _.toNumber(periodName.substring(0, 1))
      for (const timeEvent of periodInfo.timelineEventList) {
        if (!_.isNil(timeEvent.period) && _.isNil(period)) {
          period = timeEvent.period
        }
        const goalScorer = timeEvent.goalScorer
        if (!_.isNil(goalScorer)) {
          if (prevPeriodName !== periodName) {
            prevPeriodName = periodName
          } else if (prevPeriodName === periodName) {
            periodName = null
          }
          const time = timeEvent.matchTime
          const team = timeEvent.team
          let playerName = goalScorer.name
          if (_.toLower(goalScorer.method) === 'penalty') {
            playerName = `(P)${playerName}`
          }
          scoreInfoList.push({
            id: timeEvent.id,
            time, team, playerName, periodName, period, emptyClass: '',
          })
          if (team === 'home') {
            homeCount += 1
          }
          if (team === 'away') {
            awayCount += 1
          }
        }
      }
      if (homeCount > awayCount) {
        fillEmptyData(scoreInfoList, homeCount - awayCount, 'away', period)
      } else if (homeCount < awayCount) {
        fillEmptyData(scoreInfoList, awayCount - homeCount, 'home', period)
      }
    })
    return scoreInfoList
  }
  const makeBasketballScoreHistories = (): TimelineHistory[] => {
    const scoreInfoList: TimelineHistory[] = []
    let scoreHistories: { id: number, time: string, team: string, score: number, scorer: GoalScorer }[] = []
    _.forEach(periodInfoList, periodInfo => {
      for (const timeEvent of periodInfo.timelineEventList) {
        const eventType = timeEvent.type
        if (eventType === TimelineEventType.SCORE_CHANGE) {
          scoreHistories.push({
            id: timeEvent.id,
            time: timeEvent.matchClock,
            team: timeEvent.team,
            score: timeEvent.team === 'home' ? timeEvent.homeScore : timeEvent.awayScore,
            scorer: timeEvent.goalScorer,
          })
        }
      }
    })
    scoreHistories = _.orderBy(scoreHistories, ['team', 'score'], ['desc', 'asc'])
    // 타임라인 점수 기록이 시간 순서로 기록이 되지 않은 경우
    // 플레이어가 득점한 기록인 addScore 값이 음수가 되는 현상이 있어
    // 타임라인의 homeScore, awayScore 을 득점 순서 정렬 하고 homeScore 와 awayScore 기록을
    // 이전 점수와 비교하여 득점을 계산하도록 한다.
    let prevHomeScore = 0
    let prevAwayScore = 0
    for (const timeEvent of scoreHistories) {
      const goalScorer = timeEvent.scorer
      if (!_.isNil(goalScorer)) {
        const time = timeEvent.time
        const team = timeEvent.team
        const playerName = goalScorer.name

        const teamScore = timeEvent.score
        const score = team === 'home' ? teamScore - prevHomeScore : teamScore - prevAwayScore
        const existsItem = _.find(scoreInfoList, { playerName })
        if (existsItem) {
          existsItem.totalScore += score
        } else {
          scoreInfoList.push({
            id: timeEvent.id,
            time, team, playerName, totalScore: score,
          })
        }
      }
      if (timeEvent.team === 'home') {
        prevHomeScore = timeEvent.score
      } else {
        prevAwayScore = timeEvent.score
      }
    }

    return _.orderBy(scoreInfoList, ['totalScore', 'playerName'], ['desc', 'asc'])
  }

  const makeBasketballScoreHistoryTable = (team: 'home' | 'away') => {
    const scoreHistories = makeBasketballScoreHistories()
    const histories = _.filter(scoreHistories, { team })
    const elems = _.map(histories, history => {
      const name = history.playerName
      const score = history.totalScore
      return (
        <tr key={`timeline-event-table-row-home-${history.id}`}>
          <td key={`timeline-event-home-item-name-${history.id}`} className="team-name"><SportsIco
            className="sport-ico">{SPORT_ID_ICON_MAP[sportId]}</SportsIco>{name}</td>
          <td key={`timeline-event-home-item-score-${history.id}`} className="score">{score}</td>
        </tr>
      )
    })
    return elems
  }

  const makeBasketballTimelineElem = (): React.ReactElement => {
    let contents: React.ReactElement
    const homeElems = makeBasketballScoreHistoryTable('home')
    const awayElems = makeBasketballScoreHistoryTable('away')
    if (!_.isEmpty(homeElems) && !_.isEmpty(awayElems)) {
      contents = (
        <div className="timeline--table--basketball">
          <table className="table">
            <thead className="table--head">
            <tr>
              <th className="screen-out">{homeTeamName}</th>
              <th className="round">득점</th>
            </tr>
            </thead>
            <tbody className="table--body">
            {makeBasketballScoreHistoryTable('home')}
            </tbody>
          </table>
          <table className="table">
            <thead className="table--head">
            <tr>
              <th className="screen-out">{awayTeamName}</th>
              <th className="round">득점</th>
            </tr>
            </thead>
            <tbody className="table--body">
            {makeBasketballScoreHistoryTable('away')}
            </tbody>
          </table>
        </div>
      )
    }
    return contents
  }

  const makeCommonScoreHistories = (team: 'home' | 'away') => {
    let scoreHistories
    switch (sportId) {
      case SPORT_ID.ICE_HOCKEY:
        scoreHistories = makeIceHockeyScoreHistories()
        break
      case SPORT_ID.BASEBALL:
        scoreHistories = makeBaseballScoreHistories()
        break
    }
    const histories = _.filter(scoreHistories, { team })
    const elems = _.map(histories, (history, idx) => {
      const name = history.playerName
      const currentPeriod = history.period
      const nextPeriod = histories[idx + 1]?.period
      const className = classNames('match-history--item', {
        'home-team': history.team === 'home',
        'away-team': history.team === 'away',
        'baseball': SPORT_ID.BASEBALL === sportId,
        'same-round': _.isNil(history.periodName),
        'period-end': currentPeriod < nextPeriod,
      })
      const timeClassName = classNames('time', {
        icehockey: SPORT_ID.ICE_HOCKEY === sportId,
        baseball: SPORT_ID.BASEBALL === sportId,
      })

      return (
        <li key={`timeline-score-history-item-${history.id}`} className={`${className} ${history.emptyClass}`}>
          {!_.isNil(history.periodName) && <span className={timeClassName}>{history.periodName}</span>}

          <span className="player">
            {history.team === 'home' && <span className="name">{name}</span>}
            <SportsIco className="sport-ico">{SPORT_ID_ICON_MAP[sportId]}</SportsIco>
            {history.visibleIco}
            {history.team === 'away' && <span className="name">{name}</span>}
          </span>
        </li>
      )
    })
    return elems
  }

  const makeCommonTimelineElem = (): React.ReactElement => {
    let contents: React.ReactElement
    const homeElems = makeCommonScoreHistories('home')
    const awayElems = makeCommonScoreHistories('away')
    if (!_.isEmpty(homeElems) && !_.isEmpty(awayElems)) {
      contents = (
        <div className="timeline--basic common">
          <div className="match-contents">
            <ul className="match-history">
              {makeCommonScoreHistories('home')}
            </ul>
            <ul className="match-history">
              {makeCommonScoreHistories('away')}
            </ul>
          </div>
        </div>
      )
    }
    return contents
  }

  const makeSoccerHistoryTabs = (historyStats: any) => {
    return [
      {
        key: SOCCER_TAB_KEY.SCORE,
        title: (
          <div className="soccer-tab-item">
            <img src={scoreGrayImg} className="sport-ico" alt="score-icon"/>
            <img src={scoreImg} className="sport-ico-active" alt="score-icon-active"/>
            <span className="num">{historyStats.home.score + historyStats.away.score}</span>
          </div>
        ),
        name: '득점수',
        icon: SOCCER_TIMELINE_ICON_MAP[SOCCER_TIMELINE_ICON_MAP_KEY.SCORE],
      },
      {
        key: SOCCER_TAB_KEY.CORNER_KICK,
        title: (
          <div className="soccer-tab-item">
            <img src={cornerKickGrayImg} className="sport-ico" alt="corner-kick-icon"/>
            <img src={cornerKickImg} className="sport-ico-active" alt="corner-kick-icon-active"/>
            <span className="num">{historyStats.home.cornerKick + historyStats.away.cornerKick}</span>
          </div>
        ),
        name: '코너킥수',
        icon: SOCCER_TIMELINE_ICON_MAP[SOCCER_TIMELINE_ICON_MAP_KEY.CORNER_KICK],
      },
      {
        key: SOCCER_TAB_KEY.CARD,
        title: (
          <div className="soccer-tab-item">
            <img src={cardGrayImg} className="sport-ico" alt="card-icon"/>
            <img src={cardImg} className="sport-ico-active" alt="card-icon-active"/>
            <span className="num">
            {historyStats.home.yellowCard + historyStats.home.redCard
              + historyStats.away.yellowCard + historyStats.away.redCard}
          </span>
          </div>
        ),
        name: '카드수',
        icon: SOCCER_TIMELINE_ICON_MAP[SOCCER_TIMELINE_ICON_MAP_KEY.YELLOW_RED_CARD],
      },
    ]
  }

  const showCardCountGuide = () => {
    DialogUtils.showCardCountGuide()
  }

  const renderSoccerHistoryItem = (history: TimelineHistory, teamData: any) => {
    const isHomeTeam = history.team === 'home'
    const className = classNames('match-history--item', {
      'home-team': isHomeTeam,
      'away-team': !isHomeTeam,
    })

    let isCardEvent = false
    let iconKey
    switch (history.type) {
      case TimelineEventType.SCORE_CHANGE:
        iconKey = SOCCER_TIMELINE_ICON_MAP_KEY.SCORE
        break
      case TimelineEventType.CORNER_KICK:
        iconKey = SOCCER_TIMELINE_ICON_MAP_KEY.CORNER_KICK
        break
      case TimelineEventType.YELLOW_CARD:
        iconKey = SOCCER_TIMELINE_ICON_MAP_KEY.YELLOW_CARD
        isCardEvent = true
        break
      case TimelineEventType.YELLOW_RED_CARD:
        iconKey = SOCCER_TIMELINE_ICON_MAP_KEY.YELLOW_RED_CARD
        isCardEvent = true
        break
      case TimelineEventType.RED_CARD:
        iconKey = SOCCER_TIMELINE_ICON_MAP_KEY.RED_CARD
        isCardEvent = true
        break
    }

    const playerName = history.playerName
    return (
      <li
        key={`timeline-score-history-item-${history.id}`}
        className={className}
      >
        {history.matchStatusCode === MATCH_STATUS.Penalties && <span className="time">P</span>}
        {history.matchStatusCode !== MATCH_STATUS.Penalties && <span className="time">{history.time}&rsquo;</span>}
        <span className={`player${isCardEvent ? ' card' : ''}`}>
          {isHomeTeam && <span className="name">{playerName}</span>}
          <SportsIco className="sport-ico">{SOCCER_TIMELINE_ICON_MAP[iconKey]}</SportsIco>
          {!isHomeTeam && <span className="name">{playerName}</span>}
        </span>
      </li>
    )
  }

  const makeSoccerHistoryStats = (histories: TimelineHistory[]) => {
    const matchData = {
      home: {
        score: 0,
        cornerKick: 0,
        yellowCard: 0,
        redCard: 0,
      },
      away: {
        score: 0,
        cornerKick: 0,
        yellowCard: 0,
        redCard: 0,
      },
    }

    _.forEach(histories, history => {
      if (_.isEmpty(history.team)) {
        return
      }
      switch (history.type) {
        case TimelineEventType.SCORE_CHANGE:
          matchData[history.team].score += 1
          break
        case TimelineEventType.CORNER_KICK:
          matchData[history.team].cornerKick += 1
          break
        case TimelineEventType.YELLOW_CARD:
          matchData[history.team].yellowCard += 1
          break
        case TimelineEventType.RED_CARD:
        case TimelineEventType.YELLOW_RED_CARD:
          matchData[history.team].redCard += 2
          break
      }
    })
    return matchData
  }

  const makeSoccerTimelineElem = (): React.ReactElement => {
    const histories = makeSoccerHistories()
    if (_.isEmpty(histories)) {
      return null
    }

    const scoreHistories = []
    const cornerHistories = []
    const cardHistories = []

    _.forEach(histories, history => {
      if (history.type === TimelineEventType.SCORE_CHANGE) {
        scoreHistories.push(history)
      } else if (history.type === TimelineEventType.CORNER_KICK) {
        cornerHistories.push(history)
      } else {
        cardHistories.push(history)
      }
    })

    const historyStats = makeSoccerHistoryStats(histories)
    const historyTabs = makeSoccerHistoryTabs(historyStats)
    isDisabledTab = !_.isEmpty(_.find(histories, history => {
      return _.isNil(history.time) && history.matchStatusCode !== MATCH_STATUS.Penalties
    }))

    return (
      <Tabs
        tabs={historyTabs}
        animated={false}
        swipeable={false}
        onChange={(tab) => setActiveTabKey(tab.key)}
      >
        {historyTabs.map((tab) => (
          <div key={tab.key} className="timeline--basic soccer">
            <div className="match-contents">
              <ul className="match-history">
                {tab.key === SOCCER_TAB_KEY.SCORE && (
                  _.isEmpty(scoreHistories) ? (
                    <div css={{ textAlign: 'center', height: '155px' }}>
                      <AdvEmpty ico={EMPTY_ICON.TIMELINE_LIST} />
                    </div>
                  ) : (
                    scoreHistories.map((history) =>
                      renderSoccerHistoryItem(history, historyStats[history.team])
                    )
                  )
                )}
                {tab.key === SOCCER_TAB_KEY.CORNER_KICK && (
                  _.isEmpty(cornerHistories) ? (
                    <div css={{ textAlign: 'center', height: '155px' }}>
                      <AdvEmpty ico={EMPTY_ICON.TIMELINE_LIST} />
                    </div>
                  ) : (
                    cornerHistories.map((history) =>
                      renderSoccerHistoryItem(history, historyStats[history.team])
                    )
                  )
                )}
                {tab.key === SOCCER_TAB_KEY.CARD && (
                  _.isEmpty(cardHistories) ? (
                    <div css={{ textAlign: 'center', height: '155px' }}>
                      <AdvEmpty ico={EMPTY_ICON.TIMELINE_LIST} />
                    </div>
                  ) : (
                    cardHistories.map((history) =>
                      renderSoccerHistoryItem(history, historyStats[history.team])
                    )
                  )
                )}
              </ul>
            </div>
          </div>
        ))}
      </Tabs>
    )
  }

  const makeHandballScoreHistories = (): TimelineHistory[] => {
    const scoreInfoList: TimelineHistory[] = []
    _.forEach(periodInfoList, periodInfo => {
      for (const timeEvent of periodInfo.timelineEventList) {
        const goalScorer = timeEvent.goalScorer
        if (!_.isNil(goalScorer)) {
          const time = timeEvent.matchTime
          const team = timeEvent.team
          let playerName = goalScorer.name
          if (_.toLower(goalScorer.method) === 'penalty') {
            playerName = `(P)${playerName}`
          }
          scoreInfoList.push({
            id: timeEvent.id,
            time, team, playerName,
          })
        }
      }
    })
    return scoreInfoList
  }

  const makeHandballTimelineElem = (): React.ReactElement => {
    let contents: React.ReactElement
    const scoreHistories = makeHandballScoreHistories()
    if (!_.isEmpty(scoreHistories)) {
      contents = (
        <div className="timeline--basic soccer">
          <div className="match-contents">
            <ul className="match-history">
              {_.map(scoreHistories, scoreHistory => {
                const className = classNames('match-history--item', {
                  'home-team': scoreHistory.team === 'home',
                  'away-team': scoreHistory.team === 'away',
                })
                const playerName = scoreHistory.playerName
                return (
                  <li key={`timeline-score-history-item-${scoreHistory.id}`} className={className}>
                    <span className="time">{scoreHistory.time}&rsquo;</span>
                    <span className="player">
                      {scoreHistory.team === 'home' && <span className="name">{playerName}</span>}
                      <SportsIco className="sport-ico">{SPORT_ID_ICON_MAP[sportId]}</SportsIco>
                      {scoreHistory.team === 'away' && <span className="name">{playerName}</span>}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )
    }
    return contents
  }

  const timelineElems = useMemo<React.ReactElement>(() => {
    let contents: React.ReactElement
    switch (sportId) {
      case SPORT_ID.SOCCER:
        contents = makeSoccerTimelineElem()
        break
      case SPORT_ID.HANDBALL:
        contents = makeHandballTimelineElem()
        break
      case SPORT_ID.BASEBALL:
      case SPORT_ID.ICE_HOCKEY:
        contents = makeCommonTimelineElem()
        break
      case SPORT_ID.BASKETBALL:
        contents = makeBasketballTimelineElem()
        break
    }
    return contents
  }, [simpleMatchTimeline])

  const wrapClassName = classNames('timeline', {
    'timeline-none': _.isEmpty(timelineElems),
    'soccer-timeline-disabled': sportId === SPORT_ID.SOCCER && isDisabledTab,
  })

  return (
    <TimeLineWrap className={wrapClassName}>
      {timelineElems}
      {isCardTab && sportId === SPORT_ID.SOCCER &&(
        <Button
          TP
          noRadius
          className="card-count-guide-btn"
          onClick={showCardCountGuide}
        >
          <img
            src={showCardCountGuideImg}
            alt="카드수 계산법"
            className="card-count-guide-icon"
          />
          카드수 계산법
        </Button>
      )}
    </TimeLineWrap>
  )
})
